import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ComingSoonStageCard = ({ stage, index, isReversed }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`flex flex-col ${
        isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'
      } items-center gap-10 lg:gap-16`}
    >
      {/* Visual Panel */}
      <div className="w-full lg:w-1/2">
        {stage?.videoEmbed ? (
          <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ paddingBottom: '56.25%', height: 0 }}>
            <iframe
              src={stage?.videoEmbed}
              frameBorder="0"
              webkitallowfullscreen="true"
              mozallowfullscreen="true"
              allowFullScreen
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          </div>
        ) : (
          <div
            className="relative rounded-2xl overflow-hidden group"
            style={{ background: stage?.bgGradient }}
          >
            {/* Stage number badge */}
            <div className="absolute top-4 left-4 z-10">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg"
                style={{ background: stage?.accentColor, color: 'white' }}
              >
                {index + 1}
              </div>
            </div>

            {/* Main illustration area */}
            <div className="p-8 pt-16 pb-10">
              <div className="flex items-center justify-center mb-6">
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300"
                  style={{ background: stage?.accentColor }}
                >
                  <Icon name={stage?.icon} size={36} color="white" />
                </div>
              </div>

              {/* Mini process flow */}
              <div className="flex items-center justify-center gap-2 flex-wrap">
                {stage?.miniSteps?.map((step, i) => (
                  <React.Fragment key={i}>
                    <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1.5">
                      <Icon name={step?.icon} size={13} color="white" />
                      <span className="text-white text-xs font-medium">{step?.label}</span>
                    </div>
                    {i < stage?.miniSteps?.length - 1 && (
                      <Icon name="ArrowRight" size={12} color="rgba(255,255,255,0.6)" />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                {stage?.stats?.map((stat, i) => (
                  <div key={i} className="text-center bg-white/15 rounded-xl p-3">
                    <div className="text-white font-bold text-lg">{stat?.value}</div>
                    <div className="text-white/70 text-xs mt-0.5">{stat?.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Content Panel */}
      <div className="w-full lg:w-1/2">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white"
            style={{ background: stage?.accentColor }}
          >
            {index + 1}
          </div>
          <span
            className="text-sm font-semibold uppercase tracking-widest"
            style={{ color: stage?.accentColor }}
          >
            Step {index + 1}
          </span>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
          {stage?.title}
        </h2>
        <p className="text-text-secondary text-lg leading-relaxed mb-6">
          {stage?.description}
        </p>

        {/* Feature list */}
        <ul className="space-y-3 mb-6">
          {stage?.features?.map((feature, i) => (
            <li key={i} className="flex items-start gap-3">
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: stage?.accentColor + '20' }}
              >
                <Icon name="Check" size={11} color={stage?.accentColor} />
              </div>
              <span className="text-foreground text-sm leading-relaxed">{feature}</span>
            </li>
          ))}
        </ul>

        {/* Expandable details */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-medium mb-4 transition-colors duration-200"
          style={{ color: stage?.accentColor }}
        >
          <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
          {expanded ? 'Show less' : 'See how it works in detail'}
        </button>

        {expanded && (
          <div className="bg-muted rounded-xl p-5 border border-border">
            <p className="text-text-secondary text-sm leading-relaxed">{stage?.detail}</p>
          </div>
        )}

        {/* Coming soon chip instead of CTA */}
        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium" style={{ borderColor: stage?.accentColor + '40', color: stage?.accentColor, background: stage?.accentColor + '10' }}>
          <Icon name="Clock" size={14} />
          <span>Coming soon</span>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonStageCard;
